import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import AccountSecurity from "@/components/AccountSecurity";
import { usePricing, euro } from "@/lib/pricing";
import { Tag } from "@/components/Tag";
import { Check, Plug, Trash2, RotateCcw } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

// v23: correo de soporte visible dentro de la app.
const SUPPORT_EMAIL = process.env.REACT_APP_SUPPORT_EMAIL || "";

const PLANS = [
  {
    id: "gratis",
    name: "Gratis",
    tagline: "Empieza gratis",
    m: 0, y: 0,
    features: [
      "Acceso real a CaptAcción",
      "3 búsquedas al mes",
      "CRM y seguimiento",
      "Instagram",
    ],
  },
  {
    id: "pro",
    name: "Pro",
    tagline: "El favorito",
    recommended: true,
    m: 57, y: 570, disc: 17,
    features: [
      "50 búsquedas al mes",
      "Prospección con IA",
      "CRM y seguimientos",
      "Mensajes recomendados",
      "Instagram",
    ],
  },
  {
    id: "business",
    name: "Business",
    tagline: "Para equipos",
    m: 197, y: 1970, disc: 17,
    features: [
      "5 personas incluidas, cada una como un Pro",
      "Amplía el equipo con usuarios extra",
      "Bolsa común de búsquedas (50 por usuario)",
      "Todo el equipo prospecta para tu negocio",
      "Sin prospectos repetidos dentro del equipo",
      "Ves los contactos de todo el equipo",
      "Soporte prioritario",
    ],
    note: "Una cuenta madre y hasta 5 personas en total, cada una con su cuenta. Puedes añadir más personas como usuarios extra.",
  },
];

export default function Account() {
  const { user } = useAuth();
  const [busy, setBusy] = useState("");
  const [usage, setUsage] = useState(null);
  const pricing = usePricing();
  // v11: precios y oferta desde el panel admin.
  const plans = PLANS.map((pl) => (pl.id === "pro"
    ? { ...pl, m: pricing.pro.monthly, y: pricing.pro.yearly, disc: pricing.pro.yearly_discount, offer: pricing.offer }
    : pl.id === "business"
      ? { ...pl, m: pricing.business.monthly, y: pricing.business.yearly, disc: pricing.business.yearly_discount,
          extra: pricing.business.extra_user_monthly ? { m: pricing.business.extra_user_monthly, y: pricing.business.extra_user_yearly } : null }
      : pl));
  const [team, setTeam] = useState(null);
  useEffect(() => { api.get("/account/usage").then((r) => setUsage(r.data)).catch(() => setUsage(null)); }, []);
  // v13: si la cuenta es madre de un equipo Business o depende de una.
  useEffect(() => { api.get("/team").then((r) => setTeam(r.data)).catch(() => setTeam(null)); }, []);
  const leaveTeam = async () => {
    if (!window.confirm("¿Salir del equipo? Volverás a tu plan anterior, con tu propio negocio y tus propias búsquedas.")) return;
    setBusy("leave");
    try { await api.post("/team/leave"); toast.success("Has salido del equipo."); window.location.reload(); }
    catch (err) { toast.error(apiError(err)); } finally { setBusy(""); }
  };
  const isDemo = (user?.email || "").toLowerCase() === "demo@captaccion.app";
  const usedPct = usage ? Math.min(100, Math.round((usage.used / Math.max(1, usage.limit)) * 100)) : 0;

  const reset = async () => {
    setBusy("reset");
    try {
      const res = await api.post("/demo/reset");
      toast.success(`Datos de demostración restaurados (${res.data.count} contactos).`);
      window.dispatchEvent(new Event("ca:data-changed"));
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(""); }
  };

  const clear = async () => {
    setBusy("clear");
    try {
      await api.post("/demo/clear");
      toast.success("Datos borrados. Empieza con tus contactos reales.");
      window.dispatchEvent(new Event("ca:data-changed"));
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(""); }
  };

  return (
    <div className="space-y-8">
      <PageHeader title="Cuenta y plan" subtitle="Tu suscripción y los controles de tus datos." testId="account-header" />
      {SUPPORT_EMAIL && (
        <p className="-mt-2 text-sm text-brand-muted" data-testid="account-support">
          ¿Dudas, problemas o sugerencias? Escríbenos a{" "}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="font-semibold text-brand-accent hover:underline">{SUPPORT_EMAIL}</a>.
        </p>
      )}

      {/* Plan actual y consumo del mes */}
      <section data-testid="current-plan" className="rounded-lg border border-brand-accent/40 bg-brand-panel p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-brand-muted">Tu plan</p>
            <p className="mt-1 text-xl font-extrabold text-brand-ink" data-testid="current-plan-name">
              {usage?.is_demo ? "Cuenta de demostración" : (usage?.plan_label || "—")}
            </p>
            <p className="mt-1 text-sm text-brand-muted">
              {usage?.is_demo
                ? "Datos de ejemplo para explorar CaptAcción. Puedes restaurarlos o borrarlos abajo."
                : usage?.team_role === "member"
                  ? `Formas parte del equipo de una cuenta madre Business. Compartís una bolsa de ${usage.limit} búsquedas al mes.`
                  : usage?.team_role === "owner"
                    ? `Eres la cuenta madre del equipo. Bolsa común de ${usage.limit} búsquedas al mes${usage.billing ? `, facturación ${usage.billing}` : ""}.`
                : usage?.plan === "pro" && usage?.plan_gift
                  ? "Te hemos regalado el plan Pro mientras pruebas CaptAcción. Te avisaremos antes de cualquier cambio."
                  : usage?.plan === "pro"
                    ? `Tienes ${usage.limit} búsquedas al mes. Se renuevan el día 1.`
                    : `Tienes ${usage?.limit ?? ""} búsquedas al mes. Se renuevan el día 1. Pronto podrás pasar a Pro.`}
            </p>
          </div>
          <span className="rounded-md border border-brand-pos/40 bg-brand-pos/[0.12] px-2.5 py-1 text-xs font-bold text-brand-pos">Activo</span>
        </div>
        {usage && (
          <div className="mt-5" data-testid="usage-block">
            <div className="flex items-center justify-between text-sm">
              <span className="text-brand-ink">Búsquedas este mes</span>
              <span className="font-semibold text-brand-ink" data-testid="usage-count">{usage.used} de {usage.limit}</span>
            </div>
            <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-brand-panel2">
              <div className="h-full rounded-full bg-brand-accent" style={{ width: `${usedPct}%` }} />
            </div>
          </div>
        )}
      </section>

      {team?.role === "member" && (
        <section className="rounded-lg border border-brand-line bg-brand-panel p-6" data-testid="account-team-member">
          <p className="text-sm font-semibold text-brand-muted">Tu equipo</p>
          <p className="mt-1 text-sm text-brand-ink">
            Formas parte del equipo de la cuenta madre <span className="font-semibold">{team.owner?.name || team.owner?.email}</span>.
            Prospectas para su negocio y no te saldrán contactos que ya se le ofrecieron a otra persona del equipo.
          </p>
          <button type="button" onClick={leaveTeam} disabled={busy === "leave"} data-testid="account-team-leave"
                  className="mt-4 rounded-md border border-brand-line px-3 py-2 text-sm font-semibold text-brand-muted hover:text-brand-ink disabled:opacity-60">
            Salir del equipo
          </button>
        </section>
      )}
      {team?.role === "owner" && (
        <section className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-brand-line bg-brand-panel p-6" data-testid="account-team-owner">
          <div>
            <p className="text-sm font-semibold text-brand-muted">Tu equipo</p>
            <p className="mt-1 text-sm text-brand-ink">Eres la cuenta madre: {team.seats?.used} de {team.seats?.total} plazas ocupadas.</p>
          </div>
          <Link to="/app/equipo" className="rounded-md bg-brand-accent px-4 py-2 text-sm font-bold text-brand-canvas hover:brightness-110">Gestionar equipo</Link>
        </section>
      )}

      {/* Planes */}
      <section data-testid="plans-section">
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {isDemo && <span className="rounded-md border border-brand-line bg-brand-panel2 px-2.5 py-1 text-xs font-bold text-brand-muted" data-testid="plans-demo-badge">Cuenta de demostración</span>}
          <span className="rounded-md border border-brand-accent/40 bg-brand-accent/[0.10] px-2.5 py-1 text-xs font-bold text-brand-accent" data-testid="plans-status-badge">Facturación mensual o anual</span>
        </div>
        <div className="grid gap-5 lg:grid-cols-3">
          {plans.map((plan) => (
            <div
              key={plan.id}
              data-testid={`plan-card-${plan.id}`}
              className={`relative flex flex-col rounded-lg border bg-brand-panel p-6 ${plan.recommended ? "border-brand-accent shadow-[0_0_0_1px_rgba(212,175,120,0.35)]" : "border-brand-line"}`}
            >
              {plan.recommended && (
                <span data-testid="plan-recommended-badge" className="absolute -top-3 left-6 rounded-md border border-brand-accent/40 bg-brand-accent px-2.5 py-0.5 text-xs font-bold text-brand-canvas">Recomendado</span>
              )}
              <div className="flex items-center justify-between">
                <p className="text-base font-bold text-brand-ink">{plan.name}</p>
                <span className="rounded-md border border-brand-line bg-brand-canvas px-2 py-0.5 text-xs font-semibold text-brand-muted">Próximamente</span>
              </div>
              <p className="mt-1 text-xs text-brand-muted">{plan.tagline}</p>
              <div className="mt-4">
                {plan.m === 0 ? (
                  <span className="text-2xl font-extrabold tracking-tight text-brand-ink" data-testid={`plan-price-${plan.id}`}>Gratis</span>
                ) : (
                  <>
                    <div className="flex items-end gap-1.5">
                      {plan.offer && <span className="pb-0.5 text-base text-brand-muted line-through" data-testid={`plan-price-before-${plan.id}`}>{euro(plan.m)} €</span>}
                      <span className="text-2xl font-extrabold tracking-tight text-brand-ink" data-testid={`plan-price-${plan.id}`}>{euro(plan.offer ? plan.offer.monthly : plan.m)} €</span>
                      <span className="pb-0.5 text-sm text-brand-muted">/mes</span>
                    </div>
                    {plan.offer && (
                      <p className="mt-1 text-xs font-semibold text-brand-accent" data-testid={`plan-offer-${plan.id}`}>
                        {plan.offer.label}{plan.offer.ends_label ? ` · ${plan.offer.ends_label.toLowerCase()}` : ""}
                      </p>
                    )}
                    <p className="mt-1 text-xs text-brand-muted">
                      o {euro(plan.offer?.yearly || plan.y)} €/año{" "}
                      {plan.offer?.yearly ? <span className="line-through">{euro(plan.y)} €</span> : <span className="font-semibold text-brand-accent">-{plan.disc}%</span>}
                    </p>
                  </>
                )}
              </div>
              <ul className="mt-5 flex-1 space-y-2.5">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-brand-ink">
                    <Check size={16} className="mt-0.5 shrink-0 text-brand-accent" /> {f}
                  </li>
                ))}
              </ul>
              {plan.extra && (
                <p className="mt-4 text-xs text-brand-muted" data-testid="plan-extra-user">
                  Usuario extra: <span className="font-semibold text-brand-ink">{euro(plan.extra.m)} €/mes</span>
                  {plan.extra.y ? ` o ${euro(plan.extra.y)} €/año` : ""}
                </p>
              )}
              {plan.note && (
                <p className="mt-4 rounded-md border border-brand-line bg-brand-canvas p-3 text-xs leading-relaxed text-brand-muted">{plan.note}</p>
              )}
              <button
                type="button"
                disabled
                data-testid={`plan-cta-${plan.id}`}
                className="mt-5 w-full cursor-not-allowed rounded-md border border-brand-line bg-brand-panel2 px-4 py-2.5 text-sm font-bold text-brand-muted opacity-70"
              >
                Próximamente
              </button>
            </div>
          ))}
        </div>
      </section>

      <div className="grid gap-5 lg:grid-cols-2">
        {/* Datos cuenta + controles */}
        <div className="space-y-5 lg:col-span-2 lg:grid lg:grid-cols-2 lg:gap-5 lg:space-y-0">
          <div className="rounded-lg border border-brand-line bg-brand-panel p-6">
            <p className="text-sm font-semibold text-brand-muted">Datos de la cuenta</p>
            <dl className="mt-4 space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <dt className="text-brand-muted">Nombre</dt>
                <dd className="font-semibold text-brand-ink">{user?.name || "—"}</dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-brand-muted">Correo</dt>
                <dd className="font-semibold text-brand-ink">{user?.email}</dd>
              </div>
            </dl>
          </div>

          {/* Controles de demo: solo en la cuenta de demostración (borran contactos). */}
          {isDemo && (
          <div className="rounded-lg border border-brand-line bg-brand-panel p-6">
            <p className="text-sm font-semibold text-brand-ink">Datos de demostración</p>
            <p className="mt-1 text-xs leading-relaxed text-brand-muted">Vas a enseñar esto a gente. Deja los datos de ejemplo como estaban o límpialos para empezar de cero.</p>
            <div className="mt-4 flex flex-col gap-2.5 sm:flex-row">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button data-testid="account-reset-demo" className="flex flex-1 items-center justify-center gap-2 rounded-md bg-brand-accent px-4 py-2.5 text-sm font-bold text-brand-canvas transition-all duration-150 hover:brightness-110">
                    <RotateCcw size={15} /> Restaurar ejemplo
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>¿Restaurar los datos de demostración?</AlertDialogTitle>
                    <AlertDialogDescription>Se borran tus contactos actuales y vuelven los 10 de ejemplo.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction onClick={reset} disabled={busy === "reset"}>Sí, restaurar</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button data-testid="account-clear-demo" className="flex flex-1 items-center justify-center gap-2 rounded-md border border-brand-line bg-brand-panel px-4 py-2.5 text-sm font-semibold text-brand-ink transition-all duration-150 hover:border-brand-neg hover:text-brand-neg">
                    <Trash2 size={15} /> Borrar todo
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>¿Borrar todos tus contactos?</AlertDialogTitle>
                    <AlertDialogDescription>Se eliminan todos los contactos de tu CRM. Esto no se puede deshacer.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction onClick={clear} disabled={busy === "clear"}>Sí, borrar todo</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
          )}
        </div>
      </div>

      <AccountSecurity isDemo={isDemo} />

      {/* Integration point — el único sitio marcado */}
      <div className="rounded-lg border border-dashed border-brand-accent/40 bg-brand-accent/[0.04] p-6" data-testid="integration-point">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-brand-accent/[0.10] text-brand-accent">
            <Plug size={18} />
          </span>
          <div>
            <p className="font-bold text-brand-ink">Integraciones y modos de uso</p>
            <p className="mt-1 max-w-prose text-sm leading-relaxed text-brand-muted">
              Las conexiones con Meta y LinkedIn requieren integraciones oficiales, permisos y revisión de las funciones permitidas.
              {isDemo ? "Esta demostración no accede" : "CaptAcción no accede"} a cuentas sociales ni envía mensajes: CaptAcción prepara y organiza, y tú mantienes el control.
            </p>
            <div className="mt-4 grid gap-2 sm:grid-cols-2">
              {[
                ["Meta (Instagram y Facebook)", "No conectado"],
                ["LinkedIn", "No conectado"],
                ["Pagos", "Gestión manual"],
                ["Mensajería automática", "No disponible"],
                ...(isDemo ? [["Datos de demostración", "Activos"]] : []),
              ].map(([k, v]) => (
                <div key={k} className="flex items-center justify-between rounded-md border border-brand-line bg-brand-panel px-3 py-2 text-xs">
                  <span className="text-brand-muted">{k}</span>
                  <span className="font-semibold text-brand-ink">{v}</span>
                </div>
              ))}
            </div>
            <div className="mt-5 space-y-2">
              <p className="text-xs font-bold uppercase tracking-wide text-brand-muted">Modos de uso</p>
              {[
                ["Asistido", "La aplicación explica y tú ejecutas.", "funcional"],
                ["Copiloto", "La aplicación prepara y tú revisas y envías.", "funcional"],
                ["Automático", "Solo para acciones permitidas por cada red, con integraciones oficiales y permisos.", "proximamente"],
              ].map(([k, d, kind]) => (
                <div key={k} className="flex items-start justify-between gap-3 rounded-md border border-brand-line bg-brand-panel px-3 py-2">
                  <div>
                    <p className="text-sm font-semibold text-brand-ink">{k}</p>
                    <p className="text-xs text-brand-muted">{d}</p>
                  </div>
                  <Tag kind={kind === "funcional" ? "funcional" : "proximamente"} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
